// Concierge Advanced Peptide Program intake — /advanced-peptide.
//
// NOT a Beluga visit. Runs the practice's own asynchronous-telehealth workflow
// (docs/BACKEND_CONCIERGE_PROGRAMS_PRD.md): Dr. Matt Mavroson reviews every
// submission and is assigned as the patient's practitioner in the dashboard.
// Priced as a $299/month membership (Square Subscriptions, card on file).
//
// Questions are modelled on the Luma Health quiz plus Fitpal's existing
// disease / drug-history / allergy questions and the practice's PDF intake.
// Shared steps and helpers live in ./conciergeCommon.js.
import {
  PRACTICE, PRACTITIONER_IMAGE, CONSENT_VERSION, SERVED_STATES, isStateServed, stateOptions,
  ageConfirmQuestion, stateQuestion, sexAtBirthQuestion, pregnancyQuestion, heightWeightQuestion,
  cancerQuestion, diabetesQuestion, conditionOptions, conditionHistoryQuestion, conditionDetailsQuestion,
  previousTreatmentQuestions, medicationsQuestion, interactingMedicationOptions, interactingMedicationsQuestion,
  allergiesQuestion, bloodworkQuestion, additionalQuestionsQuestion, closingSteps, CONSENT_STEP_IDS,
  stateGate, applyCommonBranches, commonDisqualify, flaggedConditions, formatAnswersForSubmit as formatAnswersForFlow, consentsForSubmit,
} from './conciergeCommon';

export { PRACTICE, PRACTITIONER_IMAGE, CONSENT_VERSION, SERVED_STATES, isStateServed, stateOptions, conditionOptions, interactingMedicationOptions, CONSENT_STEP_IDS, flaggedConditions, consentsForSubmit };

export const ADVANCED_PEPTIDE_PRICE = 299;
export const ADVANCED_PEPTIDE_PLAN_SLUG = 'advanced-peptide-299';
export const ADVANCED_PEPTIDE_NAME = 'Concierge Advanced Peptide Program';
export const ADVANCED_PEPTIDE_PRIMARY_GOAL = 'advancedPeptide';
// Program copy from the practice's product page
// (doctorskinnyshot.com/products/concierge-advanced-peptide-program).
export const MEMBERSHIP_DESCRIPTION =
  'A comprehensive, physician-guided membership that combines personalized weight management, advanced peptide care, and wellness support. Members receive direct cell phone access to Dr. Mavroson, individualized treatment planning, functional medicine lab support, and ongoing care for subscribed peptide and weight-loss management.';
export const MEMBERSHIP_INCLUDES = [
  'Direct cell phone access to Dr. Mavroson for text or calls',
  'Personalized weight-management and advanced peptide care',
  'Approximately 10% member savings on eligible medications',
  'Functional medicine lab evaluation and review',
  'Member pricing on advanced hormone testing',
  'Member pricing on GI-Mapping, biological age testing (Epigenetics), microplastics testing, and other specialty lab services',
  'Preferred pricing on full-body MRI services',
  'Medication management and refill review when clinically appropriate',
  'Ongoing treatment management and clinical follow-up',
];
export const MEMBERSHIP_FOOTNOTE = 'Does not include the cost of medication. Offered exclusively in New York.';
export const PROGRAM_IMAGE = '/programs/concierge-advanced-peptide.jpg';

// ── Program-specific steps ───────────────────────────────────────────────────
const introStep = {
  id: 'ap-intro', type: 'program-overview', question: 'Concierge Advanced Peptide Program',
  subtitle: 'Monthly membership · 100% online · Expert guidance & support',
};
export const healthGoalOptions = [
  'Mental clarity & focus', 'Mood & emotional balance', 'Sharper memory', 'Anxiety & stress reduction', 'Neuroprotection',
  'Weight loss', 'Appetite control', 'Blood sugar support', 'Metabolic health', 'Body recomposition',
  'Injury recovery', 'Muscle growth', 'Joint & tendon repair', 'Exercise recovery', 'Peak physical performance',
  'Deeper sleep', 'Hormone optimization', 'Growth hormone support', 'Circadian rhythm reset', 'More energy & vitality',
  'Healthy aging', 'Mitochondrial function', 'Cellular repair', 'Longevity & lifespan', 'Immune support',
  'Inflammation reduction', 'Autoimmune balance', 'Gut healing', 'Resilience & recovery',
].map((label) => ({ label, value: label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') }))
  .concat([{ label: 'Other (please describe)', value: 'other' }]);
const healthGoalsQuestion = { id: 'health-goals', type: 'multi-select', question: 'What are your top health goals right now?', subtitle: 'Choose up to 7.', options: healthGoalOptions };
const healthGoalsOtherQuestion = { id: 'health-goals-other', type: 'textarea', question: 'Please describe your specific health goal', placeholder: 'Tell us what you want to achieve...' };
const [previousTreatmentQuestion, previousReactionQuestion, previousReactionDetailsQuestion] = previousTreatmentQuestions(
  'Have you used peptide therapy, GLP-1 medications, or hormone therapy before?',
  'For example semaglutide, tirzepatide, sermorelin, BPC-157, TRT or NAD+',
);

export const advancedPeptideFlow = [
  introStep, healthGoalsQuestion, healthGoalsOtherQuestion, ageConfirmQuestion, stateQuestion,
  sexAtBirthQuestion, pregnancyQuestion, heightWeightQuestion, cancerQuestion, diabetesQuestion,
  conditionHistoryQuestion, conditionDetailsQuestion, previousTreatmentQuestion, previousReactionQuestion, previousReactionDetailsQuestion,
  medicationsQuestion, interactingMedicationsQuestion, allergiesQuestion, bloodworkQuestion, additionalQuestionsQuestion,
  ...closingSteps,
];
// Same helper as conciergeCommon, defaulting to this program's flow.
export const formatAnswersForSubmit = (answers = {}, flow = advancedPeptideFlow) => formatAnswersForFlow(answers, flow);
export const advancedPeptideFlowById = advancedPeptideFlow.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

export const buildDynamicAdvancedPeptideFlow = (answers = {}) => {
  const gated = stateGate(advancedPeptideFlow, answers);
  if (gated) return gated;
  let flow = applyCommonBranches([...advancedPeptideFlow], answers);
  const goals = answers['health-goals'];
  if (!Array.isArray(goals) || !goals.includes('other')) flow = flow.filter((q) => q.id !== 'health-goals-other');
  return flow;
};

// Luma hard stops: under 18, pregnant/breastfeeding, active cancer, T1/T2 diabetes.
export const isDisqualifyingAnswer = (questionId, answer) =>
  commonDisqualify(questionId, answer, { diabetesStops: ['type1', 'type2'] });

export default advancedPeptideFlow;
